/**
 * Shared Membership Page Components
 * Reusable UI primitives for the membership module
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import { Icon } from './Icons';

// ─── Page Header ───────────────────────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, backHref, actions }) => {
  const handleBack = useDetailBack(backHref || '/membership');
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3 min-w-0">
        {backHref && (
          <button type="button" onClick={handleBack} aria-label="Go back"
            className="p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors">
            <Icon name="arrow-left" className="w-5 h-5" />
          </button>
        )}
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-medium text-gray-900 leading-tight">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
};

// ─── Status Badge ───────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  Active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Inactive: 'bg-gray-100 text-gray-600 border-gray-200',
  Expired: 'bg-red-100 text-red-700 border-red-200',
  expired: 'bg-red-100 text-red-700 border-red-200',
  Pending: 'bg-amber-100 text-amber-700 border-amber-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  'Under Review': 'bg-blue-100 text-blue-700 border-blue-200',
  Approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-100 text-red-700 border-red-200',
  Suspended: 'bg-orange-100 text-orange-700 border-orange-200',
  Paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Unpaid: 'bg-red-100 text-red-700 border-red-200',
  Overdue: 'bg-rose-100 text-rose-700 border-rose-200',
  Draft: 'bg-gray-100 text-gray-600 border-gray-200',
  incomplete: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  Incomplete: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  Sent: 'bg-blue-100 text-blue-700 border-blue-200',
  Completed: 'bg-teal-100 text-teal-700 border-teal-200',
  Cancelled: 'bg-red-100 text-red-700 border-red-200',
  pending_committee: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  pending_approval: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  payment_pending: 'bg-gray-100 text-gray-700 border-gray-200',
  hold: 'bg-gray-100 text-gray-700 border-gray-200',
  Hold: 'bg-gray-100 text-gray-700 border-gray-200',
  link_sent: 'bg-sky-50 text-sky-700 border-sky-200',
  removed: 'bg-gray-100 text-gray-500 border-gray-300 line-through',
  Removed: 'bg-gray-100 text-gray-500 border-gray-300 line-through',
};

const getRowTimeline = (row) => {
  if (!row) return [];
  if (row.activityLog && row.activityLog.length > 0) {
    return row.activityLog;
  }
  
  const timeline = [];
  
  // Auto fallback logs for legacy data structures
  if (row.applicationNumber) {
    timeline.push({
      action: 'created',
      performedByName: row.submittedByName || row.submittedBy || 'System User',
      performedByRole: row.submittedByRole || 'Admin',
      timestamp: row.submittedAt || row.createdAt || new Date()
    });
  } else if (row.membershipId) {
    timeline.push({
      action: 'created',
      performedByName: row.metadata?.createdByName || row.metadata?.createdBy || 'System User',
      performedByRole: row.metadata?.createdByRole || 'Admin',
      timestamp: row.createdAt || new Date()
    });
  } else if (row.invoiceNumber) {
    timeline.push({
      action: 'created',
      performedByName: row.memberName || 'System User',
      performedByRole: 'Admin',
      timestamp: row.createdAt || new Date()
    });
    if (row.status === 'Paid') {
      timeline.push({
        action: 'payment_approved',
        performedByName: 'Finance Performer',
        performedByRole: 'Finance',
        timestamp: row.paidDate || row.updatedAt
      });
    }
  } else if (row.paymentNumber || row.receiptNumber) {
    timeline.push({
      action: 'created',
      performedByName: row.processedBy || 'System User',
      performedByRole: 'Cashier',
      timestamp: row.paymentDate || row.createdAt || new Date()
    });
    if (row.status === 'Verified' || row.status === 'completed') {
      timeline.push({
        action: 'payment_approved',
        performedByName: row.processedBy || 'Finance Performer',
        performedByRole: 'Finance',
        timestamp: row.updatedAt
      });
    }
  } else {
    timeline.push({
      action: 'created',
      performedByName: 'System User',
      performedByRole: 'Admin',
      timestamp: row.createdAt || new Date()
    });
  }
  return timeline;
};

// `label` overrides the text for one screen (e.g. applications show 'pending' as "Pending Level 1"
// while payments keep "Pending"); `className` lets a column give every badge the same width.
export const StatusBadge = ({ status, label, className = '' }) => {
  let displayStatus = label || status;
  if (!label) {
    if (status === 'pending_committee') displayStatus = 'Pending Committee';
    if (status === 'pending_approval') displayStatus = 'Pending Approval';
    if (status === 'payment_pending') displayStatus = 'Hold';
    if (status === 'hold' || status === 'Hold') displayStatus = 'Hold';
    if (status === 'link_sent') displayStatus = 'Awaiting Applicant';
    if (status === 'removed') displayStatus = 'No Longer A Member';
    if (status === 'Removed') displayStatus = 'Removed';
  }
  if (typeof displayStatus === 'string') displayStatus = displayStatus.replace(/_/g, ' ');

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600 border-gray-200'} capitalize ${className}`}>
      {displayStatus}
    </span>
  );
};

/**
 * Small green member icon beside a new member's name and invoice number: never a member before (no
 * matching email, NIC or mobile, utils/memberOrigin.js on the server), so the new member discount
 * applies. Pass `show`, or `purpose` + `origin` from a membership invoice. Migrated / returning: nothing.
 */
export const NewMemberIcon = ({ show, purpose, origin, className = '' }) => {
  const isNew = show !== undefined ? !!show : (purpose === 'membership' && (origin || 'new') === 'new');
  if (!isNew) return null;
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`inline-block h-3.5 w-3.5 shrink-0 align-[-2px] text-emerald-600 ${className}`}
      role="img"
      aria-label="New member"
    >
      <title>New member: the new member discount applies</title>
      <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
    </svg>
  );
};

/**
 * Amount of a discounted invoice/payment: ~~10,000~~ 50% off 5,500 (`discount` = { percent, fullAmount }
 * from the API). Without a discount, just the amount.
 */
export const DiscountedAmount = ({ amount, discount, prefix = 'LKR ', className = '' }) => {
  const fmt = (n) => `${prefix}${Number(n || 0).toLocaleString()}`;
  if (!discount || !(discount.fullAmount > amount)) return <span className={`font-medium ${className}`}>{fmt(amount)}</span>;
  return (
    <span className={`inline-flex flex-wrap items-baseline gap-x-1.5 ${className}`} title={`${discount.percent}% off: full amount ${fmt(discount.fullAmount)}`}>
      <s className="text-xs text-gray-400">{fmt(discount.fullAmount)}</s>
      <span className="rounded bg-emerald-50 px-1 text-[0.6875rem] font-medium text-emerald-700">{discount.percent}% off</span>
      <span className="font-medium">{fmt(amount)}</span>
    </span>
  );
};

export const ActivityLogBadge = ({ value, row, activityLog }) => {
  const [showPopup, setShowPopup] = React.useState(false);
  const [popupStyle, setPopupStyle] = React.useState({});
  const triggerRef = React.useRef(null);

  // Get log/timeline entries
  const timeline = activityLog || (row ? getRowTimeline(row) : []);
  const hasHistory = timeline && timeline.length > 0;

  // Determine what to show as the label in the table cell.
  // Prefer the explicitly passed value IF it looks like a real name (not a raw 24-char hex ObjectId).
  const isRawObjectId = (v) => typeof v === 'string' && /^[a-f0-9]{24}$/i.test(v);
  const resolvedLabel = (() => {
    if (value && !isRawObjectId(value)) return value;  // explicit human name, use it
    // Fall back to the most recent timeline entry's performer name
    if (hasHistory) {
      const latest = timeline[timeline.length - 1];
      if (latest?.performedByName && !isRawObjectId(latest.performedByName)) return latest.performedByName;
    }
    // Fall back to first entry
    if (hasHistory) {
      const first = timeline[0];
      if (first?.performedByName && !isRawObjectId(first.performedByName)) return first.performedByName;
    }
    return 'Admin';
  })();

  const handleBadgeClick = (e) => {
    if (!hasHistory) return;
    e.stopPropagation();
    
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 300;
    const popoverMaxHeight = Math.min(480, window.innerHeight * 0.75);
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUp = spaceBelow < popoverMaxHeight && spaceAbove > spaceBelow;
    const left = Math.min(rect.left, window.innerWidth - popoverWidth - 12);
    
    setPopupStyle({
      position: 'fixed',
      left: `${Math.max(8, left)}px`,
      ...(openUp
        ? { bottom: `${window.innerHeight - rect.top + 6}px` }
        : { top: `${rect.bottom + 6}px` }),
      maxHeight: `${openUp ? Math.min(popoverMaxHeight, spaceAbove - 8) : Math.min(popoverMaxHeight, spaceBelow - 8)}px`,
      width: '300px'
    });
    setShowPopup(true);
  };

  const getActionInfo = (action) => {
    switch (action) {
      case 'created':
        return { label: 'Created', pill: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20' };
      case 'edited':
        return { label: 'Edited', pill: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20' };
      case 'approved_1st_level':
        return { label: 'Approved 1st Level', pill: 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20' };
      case 'approved_committee':
        return { label: 'Approved Committee Level', pill: 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/20' };
      case 'payment_approved':
        return { label: 'Payment Approved', pill: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20' };
      case 'bill_printed':
        return { label: 'Bill Printed', pill: 'bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-600/20' };
      case 'submitted':
        return { label: 'Submitted by Applicant', pill: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20' };
      case 'rejected':
        return { label: 'Rejected', pill: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-600/20' };
      case 'requested_info':
        return { label: 'Info Requested', pill: 'bg-rose-50 text-rose-600 ring-1 ring-inset ring-rose-500/20' };
      default:
        return { label: action || 'Event', pill: 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20' };
    }
  };

  return (
    <>
      <span
        ref={triggerRef}
        onClick={handleBadgeClick}
        className={`inline-flex items-center text-xs text-gray-500 font-medium ${hasHistory ? 'cursor-pointer hover:text-violet-700 transition-colors' : ''}`}
        title={hasHistory ? 'Click to view activity history' : undefined}
      >
        <span>{resolvedLabel}</span>
        {hasHistory && <Icon name="chevron-down" className="ml-1 w-3 h-3 text-gray-400" />}
      </span>

      {showPopup && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={(e) => { e.stopPropagation(); setShowPopup(false); }} />
          <div
            className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden flex flex-col"
            style={popupStyle}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-700">Activity History</span>
              <button onClick={() => setShowPopup(false)} className="text-gray-400 hover:text-gray-600 focus:outline-none">
                <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0">
              {timeline.slice().reverse().map((item, i) => {
                const info = getActionInfo(item.action);
                return (
                  <div key={i} className="flex items-start gap-2.5 px-3 py-2 border-b border-gray-50 last:border-0">
                    <div className="flex flex-col items-center mt-0.5">
                      <div className={`w-2.5 h-2.5 rounded-full ring-1 ring-inset flex-shrink-0 ${info.pill.split(' ')[0]}`} />
                      {i < timeline.length - 1 && (
                        <div className="w-px flex-1 min-h-[14px] bg-gray-100 mt-1" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${info.pill}`}>
                        {info.label}
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        By <span className="font-medium text-gray-700">{item.performedByName || 'System'}</span>
                        {item.performedByRole && <span className="text-xs bg-gray-100 text-gray-600 font-medium px-1 rounded ml-1">{item.performedByRole}</span>}
                      </p>
                      {item.notes && (
                        <p className="text-xs text-gray-500 mt-0.5 bg-gray-50/50 p-1 rounded font-medium">"{item.notes}"</p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">{new Date(item.timestamp || item.changedAt).toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-400">Current Status: <span className="font-medium text-gray-600 capitalize">{row?.status || 'Active'}</span></p>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

// ─── Table ────────────────────────────────────────────────────────────────────
// Row numbers: every table gets a "#" column (after a leading select checkbox, if any) so people
// can see how many records there are and refer to a row. `startIndex` continues the count across
// server-side pages ((page - 1) * pageSize). Pass `numbered={false}`, or define your own
// '_rowNum' column, to opt out. A column's optional `className` is added to its header and cells
// (e.g. 'px-3' for a narrow column).
const ROW_NUM_KEY = '_rowNum';

export const Table = ({ columns: baseColumns, data, loading, emptyMessage = 'No records found.', onRowClick, numbered = true, startIndex = 0, scrollKey }) => {
  const columns = numbered && !baseColumns.some(c => c.key === ROW_NUM_KEY)
    ? (() => {
        const numCol = {
          key: ROW_NUM_KEY,
          label: '#',
          render: (_, __, i) => <span className="tabular-nums text-xs font-medium text-gray-400">{startIndex + i + 1}</span>,
        };
        const at = baseColumns[0]?.key === '_select' ? 1 : 0;
        return [...baseColumns.slice(0, at), numCol, ...baseColumns.slice(at)];
      })()
    : baseColumns;

  if (loading) return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div className="flex items-center justify-center h-48">
        <div className="flex items-center gap-3 text-gray-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Loading</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <HScrollArea scrollKey={scrollKey}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={`py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap ${col.key === ROW_NUM_KEY ? 'w-10 pl-4 pr-1' : 'px-4'} ${col.className || ''}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-gray-400">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <tr key={row._id || i}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={`hover:bg-gray-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}>
                  {columns.map((col) => (
                    <td key={col.key} className={`py-3 text-sm text-gray-700 whitespace-nowrap ${col.key === ROW_NUM_KEY ? 'w-10 pl-4 pr-1' : 'px-4'} ${col.className || ''}`}>
                      {col.render ? col.render(row[col.key], row, i) : (row[col.key] ?? <span className="text-gray-400">N/A</span>)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </HScrollArea>
    </div>
  );
};

// ─── Horizontal scroll helper ─────────────────────────────────────────────────
// Wide tables are hard to scroll sideways with a plain mouse. When the content is wider than
// the box, an arrow strip appears on each side that has more to show: click it to jump, or rest
// the pointer on it to glide slowly. With a `scrollKey`, the position is remembered (also across
// a page refresh) and put back whenever the table re-renders, e.g. after reloading its data.
const HSCROLL_STORE = 'membership.tableScroll.';
const HOVER_SPEED = 3; // px per animation frame (~180 px/s)

const ScrollEdge = ({ side, onStep, onHoverStart, onHoverEnd }) => (
  <div
    className={`group/edge absolute inset-y-0 ${side === 'left' ? 'left-0 bg-gradient-to-r' : 'right-0 bg-gradient-to-l'} z-10 flex w-7 cursor-pointer items-start justify-center from-white via-white/80 to-transparent pt-2.5`}
    onMouseEnter={onHoverStart}
    onMouseLeave={onHoverEnd}
    onClick={onStep}
    role="button"
    aria-label={side === 'left' ? 'Scroll table left' : 'Scroll table right'}
    title={side === 'left' ? 'Scroll left (or rest the mouse here)' : 'Scroll right (or rest the mouse here)'}
  >
    <span className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors group-hover/edge:border-violet-300 group-hover/edge:bg-violet-600 group-hover/edge:text-white">
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d={side === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} />
      </svg>
    </span>
  </div>
);

export const HScrollArea = ({ scrollKey, children, className = '' }) => {
  const ref = React.useRef(null);
  const raf = React.useRef(0);
  const restored = React.useRef(false);
  const [edges, setEdges] = React.useState({ left: false, right: false });

  const readSaved = () => {
    if (!scrollKey) return 0;
    try { return Number(localStorage.getItem(HSCROLL_STORE + scrollKey)) || 0; } catch { return 0; }
  };
  const save = (x) => {
    if (!scrollKey) return;
    try { localStorage.setItem(HSCROLL_STORE + scrollKey, String(Math.round(x))); } catch { /* storage unavailable */ }
  };

  const update = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // Put back the remembered position once the content is wide enough to hold it
    if (!restored.current && max > 0) {
      restored.current = true;
      const saved = readSaved();
      if (saved > 0) el.scrollLeft = Math.min(saved, max);
    }
    setEdges({ left: el.scrollLeft > 2, right: el.scrollLeft < max - 2 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollKey]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) {
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    window.addEventListener('resize', update);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
      cancelAnimationFrame(raf.current);
    };
  }, [update]);

  const onScroll = () => {
    update();
    if (ref.current && restored.current) save(ref.current.scrollLeft);
  };

  const stopGlide = () => cancelAnimationFrame(raf.current);
  const glide = (dir) => {
    stopGlide();
    const tick = () => {
      const el = ref.current;
      if (!el) return;
      const before = el.scrollLeft;
      el.scrollLeft = before + dir * HOVER_SPEED;
      if (el.scrollLeft !== before) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };
  const step = (dir) => {
    stopGlide();
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {edges.left && <ScrollEdge side="left" onStep={() => step(-1)} onHoverStart={() => glide(-1)} onHoverEnd={stopGlide} />}
      <div ref={ref} onScroll={onScroll} className={`overflow-x-auto ${className}`}>
        {children}
      </div>
      {edges.right && <ScrollEdge side="right" onStep={() => step(1)} onHoverStart={() => glide(1)} onHoverEnd={stopGlide} />}
    </div>
  );
};

// ─── Search + Filter Bar ──────────────────────────────────────────────────────
export const SearchBar = ({ value, onChange, placeholder = 'Search', children }) => (
  <div className="flex flex-wrap items-center gap-3">
    <div className="relative w-full sm:flex-1 sm:max-w-md">
      <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-9 py-2 text-base sm:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          title="Clear search"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
    {children}
  </div>
);

// ─── Form Field ───────────────────────────────────────────────────────────────
export const FormField = ({ label, required, error, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1.5">{hint}</p>}
    {error && <p className="text-xs text-red-600 mt-1.5">{error}</p>}
  </div>
);

// ─── Input ────────────────────────────────────────────────────────────────────
export const Input = ({ className = '', ...props }) => (
  <input
    {...props}
    className={`w-full px-3 py-2 text-base sm:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400 ${className}`}
  />
);

// ─── Select ───────────────────────────────────────────────────────────────────
export const Select = ({ children, className = '', ...props }) => (
  <select
    {...props}
    className={`w-full px-3 py-2 text-base sm:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white disabled:bg-gray-50 ${className}`}>
    {children}
  </select>
);

// ─── Toggle (Segmented Control) ────────────────────────────────────────────────────────
export const Toggle = ({ options, value, onChange, className = '' }) => (
  <div className={`p-1 bg-gray-100 rounded-lg flex gap-1 ${className}`}>
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition-colors ${value === opt.value ? 'bg-white text-violet-700 shadow-sm ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// ─── Textarea ─────────────────────────────────────────────────────────────────
export const Textarea = ({ className = '', ...props }) => (
  <textarea
    {...props}
    rows={props.rows || 3}
    className={`w-full px-3 py-2 text-base sm:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-y ${className}`}
  />
);

// ─── Button ───────────────────────────────────────────────────────────────────
export const Button = ({ variant = 'primary', size = 'md', children, className = '', loading, ...props }) => {
  const variants = {
    primary: 'bg-violet-600 text-white hover:bg-violet-700 shadow-sm',
    secondary: 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50',
    danger: 'bg-rose-600 text-white hover:bg-rose-700 shadow-sm',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm',
    warning: 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm',
    ghost: 'text-gray-600 hover:bg-gray-100',
  };
  const sizes = { sm: 'px-3 py-1.5', md: 'px-4 py-2' };
  return (
    <button
      {...props}
      disabled={loading || props.disabled}
      className={`${sizes[size] || sizes.md} rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}>
      {loading && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
      {children}
    </button>
  );
};

// ─── Card ─────────────────────────────────────────────────────────────────────
export const Card = ({ title, subtitle, children, actions, className = '' }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>
    {(title || actions) && (
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-100">
        <div className="min-w-0">
          {title && <h3 className="text-base font-medium text-gray-900">{title}</h3>}
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    )}
    <div className="p-4 sm:p-6">{children}</div>
  </div>
);

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
export const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, confirmLabel = 'Confirm', danger }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/40 p-4">
      <div role="dialog" aria-modal="true" className="bg-white rounded-xl shadow-xl w-full max-w-md border border-gray-200">
        <div className="flex items-start gap-4 p-6">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${danger ? 'bg-rose-50 text-rose-600' : 'bg-violet-50 text-violet-600'}`}>
            <Icon name={danger ? 'trash' : 'warning'} className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-medium text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-xl">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-violet-600 hover:bg-violet-700'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────
export const EmptyState = ({ icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 mb-4">
      {typeof icon === 'string' ? <Icon name={icon} className="w-6 h-6" /> : (icon || <Icon name="inbox" className="w-6 h-6" />)}
    </div>
    <h3 className="text-base font-medium text-gray-900">{title}</h3>
    {description && <p className="text-sm text-gray-500 mt-1 max-w-xs">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);
